import {
  ApiClient,
  ApiError,
  type RunpodBranchSummary,
  type RunpodBuildPhase,
  type RunpodBuildRecord,
  type RunpodBuildStatus,
  type RunpodCustomNode,
  type RunpodNetworkVolume,
  type RunpodUploadPhase,
  type RunpodUploadRecord,
  type RunpodUploadStatus,
} from '../lib/api.js';
import {
  el,
  formatBytes,
  formatDateTime,
  formatDuration,
  formatRelativeTime,
  mount,
  statusPill,
  type PillKind,
} from '../lib/dom.js';
import { createBranchList } from './branch-list.js';
import { openModelsEditor } from './runpod-models-editor.js';
import {
  openBuildHistoryModal,
  openUploadHistoryModal,
} from './runpod-history-modals.js';

export interface RunpodImagesViewDeps {
  api: ApiClient;
  volumeEnabled?: boolean;
}

const uploadPhaseLabel: Record<RunpodUploadPhase, string> = {
  queued: 'queued',
  preparing: 'preparing script',
  creating_pod: 'creating pod',
  waiting: 'waiting for pod',
  downloading: 'downloading',
  verifying: 'verifying',
  cleanup: 'tearing down pod',
  done: 'done',
};

const uploadStatusToPill = (status: RunpodUploadStatus): PillKind => {
  if (status === 'canceled') return 'warning';
  return status as PillKind;
};

const phaseLabel: Record<RunpodBuildPhase, string> = {
  queued: 'queued',
  cloning: 'cloning git repo',
  building: 'docker build',
  pushing: 'pushing to Docker Hub',
  cleanup: 'cleaning local image',
  done: 'done',
};

const statusToPill = (status: RunpodBuildStatus): PillKind => {
  if (status === 'canceled') return 'warning';
  return status as PillKind;
};

/**
 * Friendly pill label that surfaces the current phase (BUILDING / UPLOADING TO
 * RUNPOD) while the build is running, and the final status once it's done.
 * Pill CSS uppercases the text automatically.
 */
const pillForBuild = (b: RunpodBuildRecord): { kind: PillKind; label: string } => {
  if (b.status !== 'running') {
    return { kind: statusToPill(b.status), label: b.status };
  }
  switch (b.phase) {
    case 'pushing':
    case 'cleanup':
      return { kind: 'running', label: 'uploading to runpod' };
    case 'queued':
    case 'cloning':
    case 'building':
    case 'done':
    case null:
    default:
      return { kind: 'running', label: 'building' };
  }
};

export function renderRunpodImages(deps: RunpodImagesViewDeps): HTMLElement {
  // ─── Build history & active builds card ───────────────────────
  const buildsList = el('div', { class: 'logs-list' });
  const buildsSummary = el('span', { class: 'who' });
  const buildsRefresh = el('button', { class: 'ghost', text: 'Refresh' });
  const buildsAlert = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });

  const showBuildsAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
    buildsAlert.className = kind === 'success' ? 'alert success' : 'alert';
    buildsAlert.textContent = msg;
    buildsAlert.removeAttribute('hidden');
    setTimeout(() => buildsAlert.setAttribute('hidden', ''), 6000);
  };

  // Per-row setInterval ids that tick the duration counter every second for
  // running builds. We clear them on every refresh and on view unmount to
  // avoid orphaned timers updating detached DOM nodes.
  const liveTickers: ReturnType<typeof setInterval>[] = [];
  const clearLiveTickers = () => {
    while (liveTickers.length > 0) {
      const id = liveTickers.pop();
      if (id !== undefined) clearInterval(id);
    }
  };

  // Re-poll the builds list quickly while a build is active so the row pill
  // tracks phase changes (BUILDING → UPLOADING TO RUNPOD → SUCCESS) without
  // requiring a manual refresh. Falls back to a slow safety-net poll when idle.
  let nextRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleNextRefresh = (delay: number) => {
    if (nextRefreshTimer) clearTimeout(nextRefreshTimer);
    nextRefreshTimer = setTimeout(() => void refreshBuilds(), delay);
  };

  const refreshBuilds = async () => {
    buildsRefresh.setAttribute('disabled', '');
    try {
      const { items, active } = await deps.api.listRunpodBuilds({ limit: 50 });
      buildsSummary.textContent = items.length
        ? `${items.length} recent · ${active.length} active`
        : '';
      clearLiveTickers();
      if (items.length === 0) {
        mount(buildsList, [el('div', { class: 'empty', text: 'No builds yet.' })]);
      } else {
        mount(buildsList, items.map(renderBuildRow));
      }
      scheduleNextRefresh(active.length > 0 ? 3_000 : 60_000);
    } catch (err) {
      showBuildsAlert(err instanceof ApiError ? err.message : 'Failed to load builds');
      scheduleNextRefresh(15_000);
    } finally {
      buildsRefresh.removeAttribute('disabled');
    }
  };

  const renderBuildRow = (b: RunpodBuildRecord): HTMLElement => {
    const pillInfo = pillForBuild(b);
    const head = el('div', {
      class: 'log-row-head',
      children: [
        statusPill(pillInfo.kind, pillInfo.label),
        el('span', { class: 'branch-tag', text: b.branch }),
        el('span', {
          class: 'who',
          text: `→ ${b.dockerhubRepo}:${b.dockerhubTag}`,
        }),
        b.phase && b.status === 'running'
          ? el('span', { class: 'who', text: `· ${phaseLabel[b.phase]}` })
          : null,
      ].filter((x): x is HTMLElement => x !== null),
    });
    const when = el('div', {
      class: 'when',
      text: `${formatDateTime(b.startedAt)} · ${formatDuration(b.startedAt, b.finishedAt)}`,
    });
    if (b.status === 'running') {
      const id = setInterval(() => {
        if (!when.isConnected) {
          clearInterval(id);
          return;
        }
        when.textContent = `${formatDateTime(b.startedAt)} · ${formatDuration(b.startedAt, null)}`;
      }, 1000);
      liveTickers.push(id);
    }
    const who = el('div', {
      class: 'who',
      text: [
        `by ${b.username}`,
        b.commitSha ? b.commitSha.slice(0, 7) : null,
        b.imageSize != null ? `image: ${formatBytes(b.imageSize)}` : null,
        b.imageRemoved ? 'image removed' : (b.status === 'success' ? 'image kept on VPS' : null),
        b.errorMessage,
      ].filter((x): x is string => Boolean(x)).join(' · '),
    });
    const meta = el('div', { class: 'log-meta', children: [head, when, who] });

    const viewBtn = el('button', {
      text: b.status === 'running' ? 'Watch' : 'View',
      on: {
        click: (ev) => {
          ev.stopPropagation();
          void openBuildModal(b.id);
        },
      },
    });

    const actions: HTMLElement[] = [viewBtn];
    // "Copy ref" — gives the user a one-click copy of the full
    // <repo>:<immutableTag> ready to paste into RunPod's endpoint
    // template. Only meaningful on a successful build that has an
    // immutable tag recorded.
    if (b.status === 'success' && b.immutableTag) {
      const fullRef = `${b.dockerhubRepo}:${b.immutableTag}`;
      const copyBtn = el('button', {
        text: 'Copy ref',
        attrs: { title: `Copy ${fullRef} to clipboard` },
        on: {
          click: async (ev) => {
            ev.stopPropagation();
            try {
              await navigator.clipboard.writeText(fullRef);
              const original = copyBtn.textContent;
              copyBtn.textContent = '✓ Copied';
              setTimeout(() => {
                copyBtn.textContent = original;
              }, 1500);
            } catch {
              showBuildsAlert('Could not access clipboard', 'error');
            }
          },
        },
      });
      actions.push(copyBtn);
    }
    if (b.status === 'running') {
      actions.push(
        el('button', {
          class: 'danger',
          text: 'Cancel',
          on: {
            click: async (ev) => {
              ev.stopPropagation();
              if (!confirm(`Cancel build #${b.id} (${b.branch})?`)) return;
              try {
                await deps.api.cancelRunpodBuild(b.id);
                showBuildsAlert(`Build #${b.id} canceled`, 'success');
                await refreshBuilds();
              } catch (err) {
                showBuildsAlert(err instanceof ApiError ? err.message : 'Cancel failed');
              }
            },
          },
        }),
      );
    } else if (!b.imageRemoved && (b.status === 'success' || b.phase === 'pushing' || b.phase === 'cleanup')) {
      actions.push(
        el('button', {
          class: 'danger',
          text: 'Remove image',
          attrs: {
            title: `docker rmi ${b.dockerhubRepo}:${b.dockerhubTag}`,
          },
          on: {
            click: async (ev) => {
              ev.stopPropagation();
              if (!confirm(`docker rmi ${b.dockerhubRepo}:${b.dockerhubTag}?\nThe image is already on Docker Hub; this only frees the VPS disk.`)) return;
              try {
                const res = await deps.api.removeRunpodBuildImage(b.id);
                if (res.ok) showBuildsAlert(`Image removed (${b.dockerhubRepo}:${b.dockerhubTag})`, 'success');
                else showBuildsAlert(`docker rmi failed: ${res.output || 'unknown error'}`);
                await refreshBuilds();
              } catch (err) {
                showBuildsAlert(err instanceof ApiError ? err.message : 'Remove failed');
              }
            },
          },
        }),
      );
    }

    return el('div', {
      class: 'log-row',
      on: { click: () => void openBuildModal(b.id) },
      children: [meta, el('div', { class: 'log-actions', children: actions })],
    });
  };

  // ─── Build modal with live SSE ───────────────────────────────
  const openBuildModal = async (id: number) => {
    const backdrop = el('div', { class: 'modal-backdrop' });
    const closeBtn = el('button', { class: 'ghost', text: 'Close' });
    const titleEl = el('h3', { text: `Runpod build #${id}` });
    let headerPill: HTMLElement = el('span');
    const phaseEl = el('span', { class: 'who' });
    const logBox = el('pre', { class: 'log-output', text: 'Connecting…' });
    const cancelBtn = el('button', {
      class: 'danger',
      text: 'Cancel build',
      attrs: { hidden: true },
    });
    const modal = el('div', {
      class: 'modal',
      children: [
        el('header', {
          children: [
            el('div', {
              class: 'modal-title',
              children: [titleEl, headerPill, phaseEl],
            }),
            el('div', {
              class: 'log-actions',
              children: [cancelBtn, closeBtn],
            }),
          ],
        }),
        logBox,
      ],
    });
    backdrop.append(modal);
    document.body.append(backdrop);

    let close: () => void = () => undefined;
    let stream: (() => void) | null = null;

    const finalize = () => {
      if (stream) stream();
      stream = null;
      backdrop.remove();
      document.removeEventListener('keydown', escHandler);
    };
    close = finalize;
    closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', (ev) => {
      if (ev.target === backdrop) close();
    });
    function escHandler(ev: KeyboardEvent) {
      if (ev.key === 'Escape') close();
    }
    document.addEventListener('keydown', escHandler);

    cancelBtn.addEventListener('click', async () => {
      cancelBtn.setAttribute('disabled', '');
      try {
        await deps.api.cancelRunpodBuild(id);
      } catch (err) {
        cancelBtn.removeAttribute('disabled');
        const msg = err instanceof ApiError ? err.message : 'Cancel failed';
        logBox.append(document.createTextNode(`\n!!! ${msg}\n`));
      }
    });

    // Local mirror of the build record so phase events can refresh the pill
    // without going back to the server for the full row.
    let currentBuild: RunpodBuildRecord | null = null;
    const updateHeader = (b: RunpodBuildRecord) => {
      currentBuild = b;
      titleEl.textContent = `#${b.id} · ${b.branch} → ${b.dockerhubRepo}:${b.dockerhubTag}`;
      const info = pillForBuild(b);
      const newPill = statusPill(info.kind, info.label);
      headerPill.replaceWith(newPill);
      headerPill = newPill;
    };

    let firstChunk = true;
    stream = deps.api.streamRunpodBuild(id, {
      onSnapshot: (b) => {
        updateHeader(b);
        if (b.status === 'running') cancelBtn.removeAttribute('hidden');
        else cancelBtn.setAttribute('hidden', '');
      },
      onPhase: (phase) => {
        phaseEl.textContent = `· ${phaseLabel[phase]}`;
        if (currentBuild) updateHeader({ ...currentBuild, phase });
      },
      onLog: (chunk) => {
        if (firstChunk) {
          logBox.textContent = '';
          firstChunk = false;
        }
        const wasNearBottom =
          logBox.scrollHeight - logBox.scrollTop - logBox.clientHeight < 50;
        logBox.append(document.createTextNode(chunk));
        if (wasNearBottom) logBox.scrollTop = logBox.scrollHeight;
      },
      onFinish: async (status) => {
        cancelBtn.setAttribute('hidden', '');
        phaseEl.textContent = `· ${status}`;
        await refreshBuilds();
      },
      onError: () => {
        logBox.append(document.createTextNode('\n!!! Lost connection to build stream\n'));
      },
    });
  };

  // ─── Custom nodes per-endpoint modal ────────────────────────
  /**
   * Lets the user pick which already-uploaded .zip custom nodes get
   * baked into the next image build for this endpoint, or upload a
   * brand-new .zip on the spot. The picker shows every custom node on
   * the server with a checkbox; saving sends the new full list to the
   * server in one shot.
   */
  const openCustomNodesModal = async (
    branch: string,
    onSaved: () => void,
  ): Promise<void> => {
    const backdrop = el('div', { class: 'modal-backdrop' });
    const closeBtn = el('button', { class: 'ghost', text: 'Close' });
    const saveBtn = el('button', {
      class: 'primary',
      text: 'Save',
      attrs: { disabled: true },
    });
    const list = el('div', { class: 'volume-picker-list' });
    const summary = el('span', { class: 'who' });
    const modalAlert = el('div', {
      class: 'alert',
      attrs: { hidden: true, role: 'alert' },
    });
    const fileInput = el('input', {
      attrs: { type: 'file', accept: '.zip,application/zip' },
    });
    const nameInput = el('input', {
      attrs: {
        type: 'text',
        placeholder: 'Name (e.g. ComfyUI-Manager)',
        spellcheck: false,
        autocapitalize: 'off',
        autocorrect: 'off',
      },
    });
    const uploadBtn = el('button', {
      text: 'Upload & attach',
      attrs: { type: 'button' },
    });

    const showModalAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
      modalAlert.className = kind === 'success' ? 'alert success' : 'alert';
      modalAlert.textContent = msg;
      modalAlert.removeAttribute('hidden');
      setTimeout(() => modalAlert.setAttribute('hidden', ''), 6000);
    };

    let allNodes: RunpodCustomNode[] = [];
    let initiallySelected: Set<number> = new Set();
    const selected = new Set<number>();

    const renderList = () => {
      if (allNodes.length === 0) {
        mount(list, [
          el('div', {
            class: 'empty',
            text: 'No custom nodes uploaded yet. Use the form below to upload one.',
          }),
        ]);
        return;
      }
      const rows = allNodes.map((n) => {
        const inputId = `cn_${n.id}`;
        const checkbox = el('input', {
          attrs: {
            type: 'checkbox',
            id: inputId,
            ...(selected.has(n.id) ? { checked: true } : {}),
          },
        });
        checkbox.addEventListener('change', () => {
          if ((checkbox as HTMLInputElement).checked) selected.add(n.id);
          else selected.delete(n.id);
          refreshSaveBtn();
        });
        return el('label', {
          class: 'volume-picker-row',
          attrs: { for: inputId },
          children: [
            checkbox,
            el('div', {
              class: 'volume-picker-meta',
              children: [
                el('span', { class: 'mono', text: n.name }),
                el('span', {
                  class: 'who',
                  text: [
                    formatBytes(n.sizeBytes),
                    n.description ?? null,
                    n.attachedBranches.filter((br) => br !== branch).length > 0
                      ? `also on: ${n.attachedBranches.filter((br) => br !== branch).join(', ')}`
                      : null,
                  ].filter((x): x is string => Boolean(x)).join(' · '),
                }),
              ],
            }),
          ],
        });
      });
      mount(list, rows);
    };

    const refreshSaveBtn = () => {
      const same =
        selected.size === initiallySelected.size
        && [...selected].every((id) => initiallySelected.has(id));
      if (same) saveBtn.setAttribute('disabled', '');
      else saveBtn.removeAttribute('disabled');
    };

    const refresh = async () => {
      try {
        const [{ items: all }, { items: attached }] = await Promise.all([
          deps.api.listRunpodCustomNodes(),
          deps.api.listBranchCustomNodes(branch),
        ]);
        allNodes = all;
        initiallySelected = new Set(attached.map((n) => n.id));
        selected.clear();
        for (const id of initiallySelected) selected.add(id);
        summary.textContent = all.length === 0
          ? 'No custom nodes on the server yet'
          : `${all.length} on server · ${selected.size} attached to ${branch}`;
        renderList();
        refreshSaveBtn();
      } catch (err) {
        showModalAlert(
          err instanceof ApiError ? err.message : 'Failed to load custom nodes',
        );
      }
    };

    uploadBtn.addEventListener('click', async () => {
      const file = fileInput.files?.[0];
      if (!file) {
        showModalAlert('Pick a .zip file first');
        return;
      }
      const name = nameInput.value.trim();
      if (!name) {
        showModalAlert('Set a name for this custom node');
        nameInput.focus();
        return;
      }
      uploadBtn.setAttribute('disabled', '');
      const original = uploadBtn.textContent;
      uploadBtn.textContent = `Uploading ${formatBytes(file.size)}…`;
      try {
        const { item } = await deps.api.uploadRunpodCustomNode({
          name,
          file,
          filename: file.name,
        });
        // Auto-select the freshly-uploaded one.
        selected.add(item.id);
        showModalAlert(`Uploaded "${item.name}"`, 'success');
        fileInput.value = '';
        nameInput.value = '';
        await refresh();
      } catch (err) {
        showModalAlert(err instanceof ApiError ? err.message : 'Upload failed');
      } finally {
        uploadBtn.removeAttribute('disabled');
        uploadBtn.textContent = original ?? 'Upload & attach';
      }
    });

    fileInput.addEventListener('change', () => {
      const f = fileInput.files?.[0];
      if (!f) return;
      if (!nameInput.value.trim()) {
        const base = f.name.replace(/\.zip$/i, '').replace(/[^A-Za-z0-9._-]+/g, '-');
        nameInput.value = base;
      }
    });

    saveBtn.addEventListener('click', async () => {
      saveBtn.setAttribute('disabled', '');
      try {
        await deps.api.setBranchCustomNodes(branch, [...selected]);
        onSaved();
        finalize();
      } catch (err) {
        showModalAlert(err instanceof ApiError ? err.message : 'Save failed');
        refreshSaveBtn();
      }
    });

    const modal = el('div', {
      class: 'modal',
      children: [
        el('header', {
          children: [
            el('div', {
              class: 'modal-title',
              children: [
                el('h3', { text: `Custom nodes for "${branch}"` }),
                summary,
              ],
            }),
            el('div', { class: 'log-actions', children: [saveBtn, closeBtn] }),
          ],
        }),
        modalAlert,
        el('div', {
          class: 'who',
          text:
            'These ZIPs are extracted into /ComfyUI/custom_nodes/<name>/ at '
            + 'image-build time. requirements.txt (if present) is pip-installed.',
        }),
        list,
        el('header', {
          children: [
            el('h3', { text: 'Upload a new custom node' }),
          ],
        }),
        el('div', {
          class: 'form-row',
          children: [
            el('label', {
              class: 'field',
              children: [el('span', { text: 'ZIP file' }), fileInput],
            }),
            el('label', {
              class: 'field',
              children: [el('span', { text: 'Name' }), nameInput],
            }),
            uploadBtn,
          ],
        }),
      ],
    });
    backdrop.append(modal);
    document.body.append(backdrop);

    const finalize = () => {
      backdrop.remove();
      document.removeEventListener('keydown', escHandler);
    };
    function escHandler(ev: KeyboardEvent) {
      if (ev.key === 'Escape') finalize();
    }
    document.addEventListener('keydown', escHandler);
    closeBtn.addEventListener('click', finalize);
    backdrop.addEventListener('click', (ev) => {
      if (ev.target === backdrop) finalize();
    });

    await refresh();
  };

  // ─── Branches card with mapping + Build action ────────────────
  let mappings = new Map<string, string>();

  const refreshMappings = async () => {
    try {
      const data = await deps.api.listRunpodMappings();
      mappings = new Map(data.mappings.map((m) => [m.branch, m.dockerhubRepo]));
    } catch {
      /* non-fatal — branch list still works without mappings */
    }
  };

  const branchList = createBranchList({
    title: 'Runpod endpoints',
    source: {
      load: async () => {
        await refreshMappings();
        const data = await deps.api.listRunpodBranches();
        // Server-side mapping wins (it merged them in); fall back to local cache.
        for (const b of data.branches) {
          if (b.dockerhubRepo) mappings.set(b.name, b.dockerhubRepo);
        }
        return data;
      },
      delete: (names) => deps.api.deleteRunpodBranches(names),
    },
    emptyMessage: 'No endpoints found.',
    slots: {
      rowMeta: (b) => {
        const branch = b as RunpodBranchSummary;
        const baseMeta = `${formatRelativeTime(b.lastCommitAt)} · ${b.lastAuthor ?? 'unknown'} · ${b.lastCommitMessage}`;
        const tagInfo = `tag: ${dockerTagFromBranch(b.name) ?? '(invalid)'}`;
        const repoInfo = branch.dockerhubRepo
          ? `Docker Hub: ${branch.dockerhubRepo}`
          : 'no Docker Hub repo set';
        const tplInfo = branch.runpodTemplateId
          ? `RunPod template: ${branch.runpodTemplateId} (auto-redeploy on)`
          : 'no RunPod template set';
        const epInfo = branch.runpodEndpointId
          ? `endpoint: ${branch.runpodEndpointId} (workers killed on redeploy)`
          : 'no endpoint set';
        return el('div', {
          class: 'meta',
          text: `${baseMeta} · ${repoInfo} · ${tplInfo} · ${epInfo} · ${tagInfo}`,
        });
      },
      rowActions: (b, refresh) => {
        const branch = b as RunpodBranchSummary;
        const initialRepo = branch.dockerhubRepo ?? mappings.get(b.name) ?? '';
        const initialTpl = branch.runpodTemplateId ?? '';
        const initialEp = branch.runpodEndpointId ?? '';
        const repoInput = el('input', {
          attrs: {
            type: 'text',
            placeholder: 'namespace/repo',
            value: initialRepo,
            spellcheck: false,
            autocapitalize: 'off',
            autocorrect: 'off',
            'aria-label': `Docker Hub repo for ${b.name}`,
          },
        });
        // Optional RunPod template ID. When set, every successful build push
        // also PATCHes the template's imageName at the new immutable tag,
        // triggering a rolling redeploy on attached endpoints.
        const tplInput = el('input', {
          attrs: {
            type: 'text',
            placeholder: 'RunPod template ID (optional)',
            value: initialTpl,
            spellcheck: false,
            autocapitalize: 'off',
            autocorrect: 'off',
            'aria-label': `RunPod template ID for ${b.name}`,
            title: 'Optional. When set, the build runner auto-PATCHes this template\'s imageName after a successful push, triggering a rolling redeploy of the attached serverless endpoint.',
          },
        });
        // Optional RunPod endpoint ID. When set together with the template
        // ID, the runner immediately terminates every worker on this
        // endpoint after the template PATCH — turning the default rolling
        // release (in-flight jobs finish on the OLD image) into a hard cut
        // to the new image.
        const epInput = el('input', {
          attrs: {
            type: 'text',
            placeholder: 'RunPod endpoint ID (optional)',
            value: initialEp,
            spellcheck: false,
            autocapitalize: 'off',
            autocorrect: 'off',
            'aria-label': `RunPod endpoint ID for ${b.name}`,
            title: 'Optional. When set together with the template ID, the build runner immediately terminates every worker on this endpoint after the template PATCH lands — hard cut to the new image instead of a rolling release.',
          },
        });

        const saveMapping = async () => {
          const repoVal = repoInput.value.trim();
          const tplVal = tplInput.value.trim();
          const epVal = epInput.value.trim();
          const repoChanged = repoVal !== (branch.dockerhubRepo ?? '');
          const tplChanged = tplVal !== (branch.runpodTemplateId ?? '');
          const epChanged = epVal !== (branch.runpodEndpointId ?? '');
          if (!repoChanged && !tplChanged && !epChanged) return;
          try {
            if (repoVal === '') {
              await deps.api.deleteRunpodMapping(b.name);
              mappings.delete(b.name);
              branchList.showAlert(`Cleared mapping for "${b.name}"`, 'success');
            } else {
              const { mapping } = await deps.api.setRunpodMapping(b.name, repoVal, tplVal, epVal);
              mappings.set(b.name, mapping.dockerhubRepo);
              const parts = [`→ ${mapping.dockerhubRepo}`];
              if (mapping.runpodTemplateId) parts.push(`template ${mapping.runpodTemplateId}`);
              if (mapping.runpodEndpointId) parts.push(`endpoint ${mapping.runpodEndpointId}`);
              branchList.showAlert(`Mapped "${b.name}" ${parts.join(' · ')}`, 'success');
            }
            await refresh();
          } catch (err) {
            branchList.showAlert(err instanceof ApiError ? err.message : 'Failed to save mapping');
          }
        };
        const handleBlur = () => {
          void saveMapping();
        };
        const handleEnter = (ev: KeyboardEvent) => {
          if (ev.key === 'Enter') {
            ev.preventDefault();
            void saveMapping();
            (ev.currentTarget as HTMLInputElement).blur();
          }
        };
        repoInput.addEventListener('blur', handleBlur);
        repoInput.addEventListener('keydown', handleEnter);
        tplInput.addEventListener('blur', handleBlur);
        tplInput.addEventListener('keydown', handleEnter);
        epInput.addEventListener('blur', handleBlur);
        epInput.addEventListener('keydown', handleEnter);

        const buildBtn = el('button', {
          class: 'primary',
          text: 'Build & push',
          on: {
            click: async () => {
              const repo = repoInput.value.trim();
              if (!repo) {
                branchList.showAlert('Set a Docker Hub repo before building', 'error');
                repoInput.focus();
                return;
              }
              buildBtn.setAttribute('disabled', '');
              try {
                const { build } = await deps.api.startRunpodBuild(b.name, repo);
                branchList.showAlert(`Build #${build.id} started`, 'success');
                await refreshBuilds();
                openBuildModal(build.id);
              } catch (err) {
                branchList.showAlert(err instanceof ApiError ? err.message : 'Failed to start build');
              } finally {
                buildBtn.removeAttribute('disabled');
              }
            },
          },
        });

        const editBtn = el('button', {
          text: 'Edit models',
          attrs: { title: 'Edit /ComfyUI/models/* in this branch’s Dockerfile' },
          on: {
            click: () => {
              openModelsEditor({
                api: deps.api,
                branch: b.name,
                onSaved: ({ message }) => {
                  if (message) branchList.showAlert(message, 'success');
                },
              });
            },
          },
        });

        const customNodesBtn = el('button', {
          text: 'Custom nodes',
          attrs: {
            title:
              'Pick which uploaded .zip custom nodes get extracted into '
              + '/ComfyUI/custom_nodes when this endpoint is built.',
          },
          on: {
            click: () => {
              void openCustomNodesModal(b.name, () => {
                branchList.showAlert(`Custom nodes updated for "${b.name}"`, 'success');
              });
            },
          },
        });

        const actions: HTMLElement[] = [
          repoInput, tplInput, epInput, editBtn, customNodesBtn, buildBtn,
        ];

        // Per-endpoint history modal — shows ONLY this branch's builds
        // with checkboxes / bulk delete / per-row delete. Replaces the
        // old global "Build history" card so the page doesn't grow into
        // a single ever-longer column.
        const buildsBtn = el('button', {
          text: 'Builds',
          attrs: { title: `Show build history for ${b.name}` },
          on: {
            click: () => {
              openBuildHistoryModal({
                api: deps.api,
                branch: b.name,
                openDetail: (id) => void openBuildModal(id),
              });
            },
          },
        });
        actions.push(buildsBtn);

        if (deps.volumeEnabled) {
          const uploadBtn = el('button', {
            text: 'Upload to volume',
            attrs: {
              title:
                'Spin up a CPU pod that downloads every "volume" model directly into the RunPod network volume',
            },
            on: {
              click: () => {
                void openUploadDialog(b.name);
              },
            },
          });
          actions.push(uploadBtn);

          const uploadsBtn = el('button', {
            text: 'Uploads',
            attrs: { title: `Show network-volume upload history for ${b.name}` },
            on: {
              click: () => {
                openUploadHistoryModal({
                  api: deps.api,
                  branch: b.name,
                  openDetail: (id) => void openUploadModal(id),
                });
              },
            },
          });
          actions.push(uploadsBtn);
        }

        return actions;
      },
    },
  });

  // The global "Build history" card is replaced by per-endpoint modals;
  // we keep the buildsList / refreshBuilds machinery declared for now to
  // minimise diff churn, but no longer auto-refresh on view mount and
  // never bind the click handler since the toolbar isn't mounted.

  // ─── Volume uploads card ─────────────────────────────────────
  const uploadsList = el('div', { class: 'logs-list' });
  const uploadsSummary = el('span', { class: 'who' });
  const uploadsRefresh = el('button', { class: 'ghost', text: 'Refresh' });
  const uploadsAlert = el('div', {
    class: 'alert',
    attrs: { hidden: true, role: 'alert' },
  });
  const uploadsTickers: ReturnType<typeof setInterval>[] = [];
  let uploadsNextRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  const showUploadsAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
    uploadsAlert.className = kind === 'success' ? 'alert success' : 'alert';
    uploadsAlert.textContent = msg;
    uploadsAlert.removeAttribute('hidden');
    setTimeout(() => uploadsAlert.setAttribute('hidden', ''), 6000);
  };

  const clearUploadsTickers = () => {
    while (uploadsTickers.length > 0) {
      const id = uploadsTickers.pop();
      if (id !== undefined) clearInterval(id);
    }
  };

  const scheduleNextUploadsRefresh = (delay: number) => {
    if (uploadsNextRefreshTimer) clearTimeout(uploadsNextRefreshTimer);
    uploadsNextRefreshTimer = setTimeout(() => void refreshUploads(), delay);
  };

  const renderUploadRow = (u: RunpodUploadRecord): HTMLElement => {
    const phaseLabel = u.phase ? uploadPhaseLabel[u.phase] : '';
    const pill = statusPill(uploadStatusToPill(u.status), u.status);
    const head = el('div', {
      class: 'log-row-head',
      children: [
        pill,
        el('span', { class: 'branch-tag', text: u.branch }),
        el('span', {
          class: 'who',
          text: `→ volume ${u.networkVolumeId} · ${u.dataCenterId}`,
        }),
        u.status === 'running' && phaseLabel
          ? el('span', { class: 'who', text: `· ${phaseLabel}` })
          : null,
      ].filter((x): x is HTMLElement => x !== null),
    });
    const when = el('div', {
      class: 'when',
      text: `${formatDateTime(u.startedAt)} · ${formatDuration(u.startedAt, u.finishedAt)}`,
    });
    if (u.status === 'running') {
      const id = setInterval(() => {
        if (!when.isConnected) {
          clearInterval(id);
          return;
        }
        when.textContent = `${formatDateTime(u.startedAt)} · ${formatDuration(u.startedAt, null)}`;
      }, 1000);
      uploadsTickers.push(id);
    }
    const failedSegment = u.modelsFailed > 0 ? ` · ${u.modelsFailed} failed` : '';
    const who = el('div', {
      class: 'who',
      text: [
        `by ${u.username}`,
        `${u.modelsDone}/${u.modelsTotal} models${failedSegment}`,
        u.bytesDownloaded > 0 ? formatBytes(u.bytesDownloaded) : null,
        u.errorMessage,
      ].filter((x): x is string => Boolean(x)).join(' · '),
    });
    const meta = el('div', { class: 'log-meta', children: [head, when, who] });

    const viewBtn = el('button', {
      text: u.status === 'running' ? 'Watch' : 'View',
      on: {
        click: (ev) => {
          ev.stopPropagation();
          void openUploadModal(u.id);
        },
      },
    });
    const actions: HTMLElement[] = [viewBtn];
    if (u.status === 'running') {
      actions.push(
        el('button', {
          class: 'danger',
          text: 'Cancel',
          on: {
            click: async (ev) => {
              ev.stopPropagation();
              if (!confirm(`Cancel upload #${u.id}?`)) return;
              try {
                await deps.api.cancelRunpodVolumeUpload(u.id);
                showUploadsAlert(`Upload #${u.id} canceled`, 'success');
                await refreshUploads();
              } catch (err) {
                showUploadsAlert(err instanceof ApiError ? err.message : 'Cancel failed');
              }
            },
          },
        }),
      );
    }
    return el('div', {
      class: 'log-row',
      on: { click: () => void openUploadModal(u.id) },
      children: [meta, el('div', { class: 'log-actions', children: actions })],
    });
  };

  const refreshUploads = async () => {
    if (!deps.volumeEnabled) return;
    uploadsRefresh.setAttribute('disabled', '');
    try {
      const { items, active } = await deps.api.listRunpodVolumeUploads({ limit: 30 });
      uploadsSummary.textContent = items.length
        ? `${items.length} recent · ${active.length} active`
        : '';
      clearUploadsTickers();
      if (items.length === 0) {
        mount(uploadsList, [el('div', { class: 'empty', text: 'No uploads yet.' })]);
      } else {
        mount(uploadsList, items.map(renderUploadRow));
      }
      scheduleNextUploadsRefresh(active.length > 0 ? 5_000 : 60_000);
    } catch (err) {
      showUploadsAlert(err instanceof ApiError ? err.message : 'Failed to load uploads');
      scheduleNextUploadsRefresh(15_000);
    } finally {
      uploadsRefresh.removeAttribute('disabled');
    }
  };

  const openUploadModal = async (id: number) => {
    const backdrop = el('div', { class: 'modal-backdrop' });
    const closeBtn = el('button', { class: 'ghost', text: 'Close' });
    const titleEl = el('h3', { text: `Volume upload #${id}` });
    let headerPill: HTMLElement = el('span');
    const phaseEl = el('span', { class: 'who' });
    const progressEl = el('span', { class: 'who' });
    const logBox = el('pre', { class: 'log-output', text: 'Connecting…' });
    const cancelBtn = el('button', {
      class: 'danger',
      text: 'Cancel upload',
      attrs: { hidden: true },
    });
    const modal = el('div', {
      class: 'modal',
      children: [
        el('header', {
          children: [
            el('div', {
              class: 'modal-title',
              children: [titleEl, headerPill, phaseEl, progressEl],
            }),
            el('div', { class: 'log-actions', children: [cancelBtn, closeBtn] }),
          ],
        }),
        logBox,
      ],
    });
    backdrop.append(modal);
    document.body.append(backdrop);

    let stream: (() => void) | null = null;
    const finalize = () => {
      if (stream) stream();
      stream = null;
      backdrop.remove();
      document.removeEventListener('keydown', escHandler);
    };
    closeBtn.addEventListener('click', finalize);
    backdrop.addEventListener('click', (ev) => {
      if (ev.target === backdrop) finalize();
    });
    function escHandler(ev: KeyboardEvent) {
      if (ev.key === 'Escape') finalize();
    }
    document.addEventListener('keydown', escHandler);

    cancelBtn.addEventListener('click', async () => {
      cancelBtn.setAttribute('disabled', '');
      try {
        await deps.api.cancelRunpodVolumeUpload(id);
      } catch (err) {
        cancelBtn.removeAttribute('disabled');
        const msg = err instanceof ApiError ? err.message : 'Cancel failed';
        logBox.append(document.createTextNode(`\n!!! ${msg}\n`));
      }
    });

    let firstChunk = true;
    let currentTotal = 0;
    const updateHeader = (u: RunpodUploadRecord) => {
      titleEl.textContent = `#${u.id} · ${u.branch} → ${u.networkVolumeId}`;
      currentTotal = u.modelsTotal;
      const newPill = statusPill(uploadStatusToPill(u.status), u.status);
      headerPill.replaceWith(newPill);
      headerPill = newPill;
      progressEl.textContent = `· ${u.modelsDone}/${u.modelsTotal} models${u.modelsFailed > 0 ? ` (${u.modelsFailed} failed)` : ''}${u.bytesDownloaded > 0 ? ` · ${formatBytes(u.bytesDownloaded)}` : ''}`;
    };

    stream = deps.api.streamRunpodVolumeUpload(id, {
      onSnapshot: (u) => {
        updateHeader(u);
        if (u.status === 'running') cancelBtn.removeAttribute('hidden');
        else cancelBtn.setAttribute('hidden', '');
      },
      onPhase: (phase) => {
        phaseEl.textContent = `· ${uploadPhaseLabel[phase]}`;
      },
      onProgress: (info) => {
        progressEl.textContent = `· ${info.modelsDone}/${currentTotal} models${info.modelsFailed > 0 ? ` (${info.modelsFailed} failed)` : ''}${info.bytesDownloaded > 0 ? ` · ${formatBytes(info.bytesDownloaded)}` : ''}`;
      },
      onLog: (chunk) => {
        if (firstChunk) {
          logBox.textContent = '';
          firstChunk = false;
        }
        const wasNearBottom =
          logBox.scrollHeight - logBox.scrollTop - logBox.clientHeight < 50;
        logBox.append(document.createTextNode(chunk));
        if (wasNearBottom) logBox.scrollTop = logBox.scrollHeight;
      },
      onFinish: async (status) => {
        cancelBtn.setAttribute('hidden', '');
        phaseEl.textContent = `· ${status}`;
        await refreshUploads();
      },
      onError: () => {
        logBox.append(document.createTextNode('\n!!! Lost connection to upload stream\n'));
      },
    });
  };

  /**
   * "Upload to volume" button handler — fetches the available network volumes
   * for the user's RunPod account, prompts for a choice, then kicks off the
   * job. Picking a volume also pins the pod's datacenter (the volume is bound
   * to one DC, the pod must be created in the same DC).
   */
  const openUploadDialog = async (branch: string) => {
    if (!deps.volumeEnabled) {
      branchList.showAlert(
        'Network volume upload is disabled (RUNPOD_API_KEY not set on the server)',
      );
      return;
    }
    let volumes: RunpodNetworkVolume[];
    try {
      const data = await deps.api.listRunpodNetworkVolumes();
      volumes = data.volumes;
    } catch (err) {
      branchList.showAlert(
        err instanceof ApiError ? err.message : 'Failed to fetch network volumes',
      );
      return;
    }
    if (volumes.length === 0) {
      branchList.showAlert('No network volumes found on this RunPod account');
      return;
    }

    // Multi-select picker modal — list of volumes as a checkbox group
    // (mass-sync across datacenters) + Sync button. Each picked volume
    // gets its own upload pod that mirrors the branch's volume manifest
    // (downloads missing files AND removes any model on the volume not
    // declared in the manifest).
    const backdrop = el('div', { class: 'modal-backdrop' });
    const startBtn = el('button', {
      class: 'primary',
      text: 'Sync',
      attrs: { disabled: true },
    });
    const cancelBtn = el('button', { class: 'ghost', text: 'Cancel' });
    const selected = new Set<string>();
    const refreshStartBtn = () => {
      if (selected.size === 0) {
        startBtn.setAttribute('disabled', '');
        startBtn.textContent = 'Sync';
      } else {
        startBtn.removeAttribute('disabled');
        startBtn.textContent = selected.size === 1
          ? 'Sync 1 volume'
          : `Sync ${selected.size} volumes`;
      }
    };
    const list = el('div', { class: 'volume-picker-list' });
    for (const v of volumes) {
      const id = `vol_${v.id}`;
      const checkbox = el('input', {
        attrs: { type: 'checkbox', name: 'volpick', id, value: v.id },
      });
      checkbox.addEventListener('change', () => {
        if ((checkbox as HTMLInputElement).checked) selected.add(v.id);
        else selected.delete(v.id);
        refreshStartBtn();
      });
      list.append(
        el('label', {
          class: 'volume-picker-row',
          attrs: { for: id },
          children: [
            checkbox,
            el('div', {
              class: 'volume-picker-meta',
              children: [
                el('span', { class: 'mono', text: v.name || v.id }),
                el('span', {
                  class: 'who',
                  text: `${v.dataCenterId} · ${formatBytes(v.size * 1024 * 1024 * 1024)}`,
                }),
              ],
            }),
          ],
        }),
      );
    }
    const modal = el('div', {
      class: 'modal',
      children: [
        el('header', {
          children: [
            el('div', {
              class: 'modal-title',
              children: [el('h3', { text: `Sync volume manifest to network volume(s)` })],
            }),
            el('div', { class: 'log-actions', children: [cancelBtn, startBtn] }),
          ],
        }),
        el('div', {
          class: 'who',
          text:
            `Branch "${branch}" — sync mirrors the manifest: missing files are downloaded, `
            + `extras (model files NOT in the manifest) are removed. Pick one or several volumes `
            + `to mass-sync endpoints adossed to multiple datacenters.`,
        }),
        list,
      ],
    });
    backdrop.append(modal);
    document.body.append(backdrop);

    const close = () => {
      backdrop.remove();
      document.removeEventListener('keydown', escHandler);
    };
    function escHandler(ev: KeyboardEvent) {
      if (ev.key === 'Escape') close();
    }
    document.addEventListener('keydown', escHandler);
    cancelBtn.addEventListener('click', close);
    backdrop.addEventListener('click', (ev) => {
      if (ev.target === backdrop) close();
    });
    startBtn.addEventListener('click', async () => {
      if (selected.size === 0) return;
      startBtn.setAttribute('disabled', '');
      const ids = [...selected];
      try {
        const { uploads } = await deps.api.startRunpodVolumeUpload(branch, ids);
        close();
        await refreshUploads();
        if (uploads.length === 1) {
          // Single-volume: keep the original UX and pop the live log.
          branchList.showAlert(`Sync #${uploads[0]!.id} started`, 'success');
          void openUploadModal(uploads[0]!.id);
        } else {
          // Multi-volume: don't auto-open any modal (which one would we
          // pick?) — surface the list of started syncs in the alert and
          // let the user click into each from the Uploads history.
          branchList.showAlert(
            `${uploads.length} syncs started: ${uploads.map((u) => `#${u.id}`).join(', ')}`,
            'success',
          );
        }
      } catch (err) {
        refreshStartBtn();
        branchList.showAlert(
          err instanceof ApiError ? err.message : 'Failed to start sync',
        );
      }
    });
  };

  // Same as the build history above: the global "Network volume uploads"
  // card is gone — see openUploadHistoryModal triggered from the row's
  // "Uploads" button.

  // Allow scoped CSS overrides for this view (the meta line is longer and the
  // per-row actions include a text input — the default `.branch` rules make
  // them overflow without these tweaks).
  branchList.root.classList.add('runpod-branches');

  // Build & upload history are now per-endpoint modals (opened from the
  // "Builds" / "Uploads" buttons on each row), not stacked global cards.
  // The view itself is now just the endpoints list.
  const view = el('div', { children: [branchList.root] });

  // Cleanup any timers / DOM observers if the view gets unmounted. The
  // global builds/uploads list state still exists in scope (we keep its
  // declarations to avoid touching unrelated code paths) but it's no
  // longer mounted, so its tickers naturally never fire from inside a
  // detached node — we still clear the next-refresh timers defensively.
  const observer = new MutationObserver(() => {
    if (!view.isConnected) {
      if (nextRefreshTimer) clearTimeout(nextRefreshTimer);
      if (uploadsNextRefreshTimer) clearTimeout(uploadsNextRefreshTimer);
      clearLiveTickers();
      clearUploadsTickers();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return view;
}

function dockerTagFromBranch(branch: string): string | null {
  let tag = branch
    .replace(/[/\s]+/g, '-')
    .replace(/[^a-zA-Z0-9_.-]/g, '-')
    .replace(/^[.-]+/, '')
    .slice(0, 128);
  while (tag.endsWith('.') || tag.endsWith('-')) tag = tag.slice(0, -1);
  return tag.length > 0 ? tag : null;
}
